import { createItem, deleteItem, getItem, listItems, updateItem } from './sharepoint';
import {
  DAY_KEYS,
  OT_RATE_MULTIPLIER,
  STATUS as TIMESHEET_STATUS,
} from './timesheets';
import { findAssignment, getRateFor } from './suppliers';
import {
  MILEAGE_RATE_USD_PER_MILE,
  calcMileageDollars,
} from '../config/rates';

// Display order for the per-type labor lines on the By Supplier card:
// Standard Liaison Hours first, then OT, then any legacy 'Other'.
const LABOR_TYPE_ORDER = { Standard: 0, Overage: 1, Other: 2 };

// Coverage statuses that may be billed. A plant assignment in any other
// status (Proposal Out, Prospecting, Paused, Suspended, Closed, Declined)
// is never priced — see billableBlocked() below. This is the billing-side
// half of the proposal pipeline: a "Proposal Out" plant can't generate an
// invoice even if a stray timesheet somehow references it.
const BILLABLE_STATUSES = new Set(['Active', 'On Call']);

// True only when we can POSITIVELY confirm the supplier's assignment for this
// plant is in a non-billable status. Unknown supplier or unknown assignment
// returns false so those hours still fall through to the existing "orphan /
// missing rate" path (that signal is useful and we don't want to hide it).
function billableBlocked(supplier, plantCode, service) {
  if (!supplier) return false;
  // `service` selects between several rows at the same plant (a supplier
  // may bill e.g. Standard Coverage and Lineside Support at one plant at
  // different rates — Sep 2026). Omitted → the plant's only/default row.
  const pa = findAssignment(supplier, plantCode, service);
  if (!pa) return false;
  const status = pa.coverageStatus || 'Active';
  return !BILLABLE_STATUSES.has(status);
}

// ---- Timesheet-driven billing aggregator -------------------------------
// The Billing dashboard is a read-only view over approved timesheets, not
// a write layer — these aggregator helpers do all the math in one place
// so the page stays simple and so the Invoicing page can reuse the same
// numbers when it ships. Below the aggregator, the legacy Billing-list
// CRUD helpers remain in place for any callers that still reference
// them; they're scheduled for retirement once Invoicing lands.

/**
 * Billing aggregator — takes the full list of timesheets plus the full
 * supplier list and a date range, returns a structured breakdown shaped
 * for the Billing dashboard. Only Status='Approved' timesheets bill.
 * Labor uses per-day rate lookups via getRateFor() so a week that
 * straddles a rate change is priced correctly on each side.
 *
 * Shape returned:
 *   {
 *     suppliers: [
 *       {
 *         supplierCode, supplierName,
 *         plants: [
 *           { plantCode, laborHours, laborDollars, miles,
 *             mileageDollars, expenseDollars, orphanHours, total }
 *         ],
 *         supplierTotal,
 *       }
 *     ],
 *     grandTotal,
 *     range: { startDate, endDate },
 *     missingRatePairs: [{ supplierCode, plantCode }, ...],
 *     mileageRateUsed,
 *   }
 */
export function aggregateBilling(timesheets, suppliers, range, team) {
  const startIso = toIso(range?.startDate);
  const endIso = toIso(range?.endDate);

  const supplierByCode = new Map();
  for (const s of suppliers || []) {
    if (s.SupplierCode) supplierByCode.set(s.SupplierCode, s);
  }

  // Submitter → liaisonManagers lookup. Built once off the roster so
  // the per-row loop stays cheap. Keyed by lowercase email first (most
  // reliable for cross-referencing timesheets) and full name second
  // (fallback for rows missing an email). The same member's LMs end
  // up under both keys.
  const lmsBySubmitter = new Map();
  for (const member of team || []) {
    const lms = Array.isArray(member.liaisonManagers)
      ? member.liaisonManagers.filter(Boolean)
      : [];
    if (lms.length === 0) continue;
    const email = (member.Email || '').trim().toLowerCase();
    if (email) lmsBySubmitter.set(email, lms);
    const name = [member.FirstName, member.LastName]
      .filter(Boolean)
      .join(' ')
      .trim()
      .toLowerCase();
    if (name) lmsBySubmitter.set(name, lms);
    const title = (member.Title || '').trim().toLowerCase();
    if (title) lmsBySubmitter.set(title, lms);
  }
  const lookupLiaisonManagers = (ts) => {
    const email = (ts.SubmitterEmail || '').trim().toLowerCase();
    if (email && lmsBySubmitter.has(email)) return lmsBySubmitter.get(email);
    const name = (ts.SubmitterName || '').trim().toLowerCase();
    if (name && lmsBySubmitter.has(name)) return lmsBySubmitter.get(name);
    return [];
  };

  // Nested map: supplierCode → plantCode → totals bucket (supplier view).
  const agg = new Map();
  // Flat map keyed by `${submitterKey}|${supplierCode}|${plantCode}` —
  // powers the Proxemics Team Billing breakdown. Keyed by submitter
  // email (lowercased) + supplier + plant so the same person working
  // two different plants or two suppliers shows as distinct rows.
  const teamAgg = new Map();
  // Flat map keyed by regional-manager display name — powers the By
  // Regional Manager breakdown. The RM is denormalized onto each
  // timesheet at submission time so later roster changes don't orphan
  // historical numbers.
  const rmAgg = new Map();
  // Flat map keyed by liaison-manager display name — powers the By
  // Customer Liaison Manager breakdown. Because a submitter can have
  // multiple LMs, their hours appear under each LM (intentional —
  // this is a per-LM view, not a grand-total rollup).
  const lmAgg = new Map();
  const missingRateKeys = new Set();

  const getBucket = (supplierCode, plantCode) => {
    if (!agg.has(supplierCode)) agg.set(supplierCode, new Map());
    const plants = agg.get(supplierCode);
    if (!plants.has(plantCode)) {
      plants.set(plantCode, {
        laborHours: 0,
        laborDollars: 0,
        miles: 0,
        mileageDollars: 0,
        expenseDollars: 0,
        orphanHours: 0,
        // Per-rate breakdown so the UI can show "24 hrs × $45 + 16 hrs
        // × $50" when a supplier's rate changed mid-period, instead of
        // a misleading effective-average rate. Keyed by rate value as
        // a string ("45" / "50") to avoid floating-point key flakiness.
        ratesByRate: new Map(),
        // Labor split by coverage type so the By Supplier card can show
        // Standard Liaison Hours and OT Liaison Hours on separate lines
        // (per Jillian, Aug 2026). Keyed by coverageType → {hours, dollars}.
        // Hours are the actual worked hours (OT is NOT inflated — its 1.5×
        // premium rides on the rate); the combined laborHours/laborDollars
        // above stay untouched for the roll-up tables and totals.
        laborByType: new Map(),
      });
    }
    return plants.get(plantCode);
  };

  // Increment the per-rate bucket for a plant. Isolated as a helper so
  // the main loop stays readable.
  const bumpRate = (bucket, rate, hours) => {
    const key = String(rate);
    const existing = bucket.ratesByRate.get(key) || {
      rate,
      hours: 0,
      dollars: 0,
    };
    existing.hours += hours;
    existing.dollars += hours * rate;
    bucket.ratesByRate.set(key, existing);
  };

  // Team-member bucket lookup — same pattern as getBucket above, but
  // scoped per (submitter, supplier, plant) triple so the team
  // breakdown doesn't need a second pass over the timesheets.
  const getTeamBucket = (ts, supplierCode, plantCode) => {
    // Submitter key: prefer id (stable even after name/email tweaks),
    // fall back to email so legacy rows still aggregate correctly.
    const submitterKey = String(
      ts.SubmitterId || ts.SubmitterEmail || ts.SubmitterName || '',
    ).toLowerCase();
    const key = `${submitterKey}|${supplierCode}|${plantCode}`;
    if (!teamAgg.has(key)) {
      teamAgg.set(key, {
        submitterKey,
        submitterName: ts.SubmitterName || ts.SubmitterEmail || 'Unknown',
        submitterEmail: ts.SubmitterEmail || '',
        supplierCode,
        plantCode,
        laborHours: 0,
        laborDollars: 0,
        miles: 0,
        mileageDollars: 0,
        expenseDollars: 0,
        orphanHours: 0,
        // Collect every timesheet's SubmittedAt that feeds this bucket
        // so the UI can render the submission date (or range) in the
        // team billing table. Set avoids duplicates when one timesheet
        // has multiple matching rows.
        submittedAts: new Set(),
        // Parallel collection of contributing timesheet ids so the
        // team table can deep-link the date cell to the Approvals
        // page. Stored as a Map<id, submittedAt> so we can pick the
        // earliest-submitted one as the primary link target without
        // a second pass.
        timesheetIds: new Map(),
      });
    }
    const bucket = teamAgg.get(key);
    if (ts.SubmittedAt) bucket.submittedAts.add(ts.SubmittedAt);
    if (ts.id != null) {
      bucket.timesheetIds.set(String(ts.id), ts.SubmittedAt || '');
    }
    return bucket;
  };

  // Liaison-manager bucket lookup — returns the array of LM buckets
  // (possibly empty) that this timesheet's submitter rolls up into.
  // Returning an array keeps the call site simple: `for (const b of
  // getLmBuckets(ts)) { b.field += value }`. Submitters with no LMs
  // fall into an "Unassigned" bucket so their hours still show up.
  const getLmBuckets = (ts) => {
    const lms = lookupLiaisonManagers(ts);
    const names = lms.length > 0 ? lms : ['Unassigned'];
    const buckets = [];
    for (const rawName of names) {
      const lm = (rawName || '').trim() || 'Unassigned';
      if (!lmAgg.has(lm)) {
        lmAgg.set(lm, {
          liaisonManager: lm,
          submitters: new Set(),
          plantCodes: new Set(),
          laborHours: 0,
          laborDollars: 0,
          miles: 0,
          mileageDollars: 0,
          expenseDollars: 0,
          orphanHours: 0,
        });
      }
      const bucket = lmAgg.get(lm);
      if (ts.SubmitterName) bucket.submitters.add(ts.SubmitterName);
      buckets.push(bucket);
    }
    return buckets;
  };

  // Regional-manager bucket lookup — one flat bucket per RM name,
  // accumulated across every submitter / supplier / plant they oversee.
  // Timesheets with no RM stamped fall into an "Unassigned" bucket so
  // those hours still show up in the dashboard.
  const getRmBucket = (ts) => {
    const rm = (ts.RegionalManager || '').trim() || 'Unassigned';
    if (!rmAgg.has(rm)) {
      rmAgg.set(rm, {
        regionalManager: rm,
        submitters: new Set(),
        plantCodes: new Set(),
        laborHours: 0,
        laborDollars: 0,
        miles: 0,
        mileageDollars: 0,
        expenseDollars: 0,
        orphanHours: 0,
      });
    }
    const bucket = rmAgg.get(rm);
    if (ts.SubmitterName) bucket.submitters.add(ts.SubmitterName);
    return bucket;
  };

  for (const ts of timesheets || []) {
    if ((ts.Status || '') !== TIMESHEET_STATUS.APPROVED) continue;
    const weekStart = ts.WeekStart;
    if (!weekStart) continue;

    // (The >40-hrs OT surcharge tracking that used to live here was
    // removed Aug 2026 — see the note where the post-pass used to run.)

    for (const row of ts.rows || []) {
      const supplierCode = row.supplierCode;
      const plantCode = row.plantCode;
      if (!supplierCode || !plantCode) continue;
      const coverageType = row.coverageType || 'Standard';
      const supplier = supplierByCode.get(supplierCode);

      // Expenses are a single dollar amount per row, not a per-day
      // metric. Attribute the whole amount to the week-start date —
      // if that date falls in the requested range, include it.
      if (coverageType === 'Expenses') {
        if (!inRange(weekStart, startIso, endIso)) continue;
        const amount = Number(row.expenseAmount) || 0;
        if (amount > 0) {
          getBucket(supplierCode, plantCode).expenseDollars += amount;
          getTeamBucket(ts, supplierCode, plantCode).expenseDollars += amount;
          const rmBucket = getRmBucket(ts);
          rmBucket.expenseDollars += amount;
          rmBucket.plantCodes.add(plantCode);
          for (const lmBucket of getLmBuckets(ts)) {
            lmBucket.expenseDollars += amount;
            lmBucket.plantCodes.add(plantCode);
          }
        }
        continue;
      }

      // Per-day iteration for Standard / Overage / Other / Mileage.
      const values = row.values || {};
      for (let dayIdx = 0; dayIdx < 7; dayIdx += 1) {
        const dayKey = DAY_KEYS[dayIdx];
        const value = Number(values[dayKey]) || 0;
        if (value <= 0) continue;
        const dayIso = addDays(weekStart, dayIdx);
        if (!inRange(dayIso, startIso, endIso)) continue;

        if (coverageType === 'Mileage') {
          const bucket = getBucket(supplierCode, plantCode);
          const dollars = calcMileageDollars(value);
          bucket.miles += value;
          bucket.mileageDollars += dollars;
          const teamBucket = getTeamBucket(ts, supplierCode, plantCode);
          teamBucket.miles += value;
          teamBucket.mileageDollars += dollars;
          const rmBucket = getRmBucket(ts);
          rmBucket.miles += value;
          rmBucket.mileageDollars += dollars;
          rmBucket.plantCodes.add(plantCode);
          for (const lmBucket of getLmBuckets(ts)) {
            lmBucket.miles += value;
            lmBucket.mileageDollars += dollars;
            lmBucket.plantCodes.add(plantCode);
          }
        } else {
          // Standard / Overage / Other → hours × rate-in-effect.
          // Skip pricing entirely when the plant's coverage status isn't
          // billable (Proposal Out, Paused, etc.) — see billableBlocked().
          // Only suppresses when we can confirm a non-billable status; an
          // unknown supplier/assignment still falls through to orphan below.
          // The row's `service` picks which of the supplier's rows at this
          // plant prices these hours (blank on legacy rows → default row).
          if (billableBlocked(supplier, plantCode, row.service)) continue;
          const rate = supplier
            ? getRateFor(supplier, plantCode, dayIso, row.service)
            : null;
          const bucket = getBucket(supplierCode, plantCode);
          const teamBucket = getTeamBucket(ts, supplierCode, plantCode);
          const rmBucket = getRmBucket(ts);
          rmBucket.plantCodes.add(plantCode);
          const lmBuckets = getLmBuckets(ts);
          for (const lmBucket of lmBuckets) lmBucket.plantCodes.add(plantCode);
          if (rate != null) {
            // OT Liaison Hours (coverage type 'Overage') are billed at
            // time-and-a-half by charging the RATE at 1.5× (per Jillian,
            // Aug 2026) — the HOURS stay as actually worked (5 worked → 5
            // hrs @ 1.5× rate). laborDollars is IDENTICAL to the prior
            // "raw × 1.5 hours @ standard rate" (the premium just moved
            // from the hours back to the rate), so every rollup still
            // reconciles; OT now appears as its own 1.5× rate line in the
            // breakdown. billRate rounds to 2 dp for a clean rate value —
            // exact for whole-dollar base rates.
            const isOT = coverageType === 'Overage';
            const billHours = value;
            const billRate = isOT
              ? Math.round(rate * OT_RATE_MULTIPLIER * 100) / 100
              : rate;
            bucket.laborHours += billHours;
            bucket.laborDollars += billHours * billRate;
            bumpRate(bucket, billRate, billHours);
            // Split by coverage type for the per-type lines on the card.
            const ltKey = coverageType || 'Standard';
            const lt = bucket.laborByType.get(ltKey) || { hours: 0, dollars: 0 };
            lt.hours += billHours;
            lt.dollars += billHours * billRate;
            bucket.laborByType.set(ltKey, lt);
            teamBucket.laborHours += billHours;
            teamBucket.laborDollars += billHours * billRate;
            rmBucket.laborHours += billHours;
            rmBucket.laborDollars += billHours * billRate;
            for (const lmBucket of lmBuckets) {
              lmBucket.laborHours += billHours;
              lmBucket.laborDollars += billHours * billRate;
            }
          } else {
            // No rate found for this supplier/plant/day — still track
            // the hours so nothing disappears silently, and flag the
            // pair so the director knows to set the missing rate.
            bucket.orphanHours += value;
            teamBucket.orphanHours += value;
            rmBucket.orphanHours += value;
            for (const lmBucket of lmBuckets) lmBucket.orphanHours += value;
            missingRateKeys.add(`${supplierCode}|${plantCode}`);
          }
        }
      }
    }

    // The automatic >40-hrs/week overtime surcharge was REMOVED (Jillian,
    // Aug 2026): hours over 40 now bill at the normal rate, with no alert
    // and no "Bill X Hours at OT Rate" approval. Overtime is captured only
    // via the dedicated OT Liaison Hours ('Overage') card, which bills at
    // 1.5× (see the billHours block above). tsLaborByPair / otApprovedKeys
    // are no longer used.
  }

  const suppliersOut = [...agg.entries()]
    .map(([supplierCode, plants]) => {
      const plantsOut = [...plants.entries()]
        .map(([plantCode, t]) => {
          // Flatten the per-rate Map into a sorted array (cheapest rate
          // first) so the UI can render a stable breakdown even across
          // re-aggregations. Rates are exact; hours/dollars are rounded
          // to match the top-level precision.
          const rateBreakdown = [...t.ratesByRate.values()]
            .map((r) => ({
              rate: r.rate,
              hours: round2(r.hours),
              dollars: round2(r.dollars),
            }))
            .sort((a, b) => a.rate - b.rate);
          // Per-coverage-type labor lines (Standard / OT / Other) for the
          // By Supplier card. Each carries its own effective rate
          // (dollars ÷ hours). Standard first, then OT, then anything else.
          const laborLines = [...t.laborByType.entries()]
            .map(([coverageType, v]) => ({
              coverageType,
              hours: round2(v.hours),
              dollars: round2(v.dollars),
              rate: v.hours > 0 ? Math.round((v.dollars / v.hours) * 100) / 100 : 0,
            }))
            .filter((l) => l.hours > 0)
            .sort(
              (a, b) =>
                (LABOR_TYPE_ORDER[a.coverageType] ?? 9) -
                (LABOR_TYPE_ORDER[b.coverageType] ?? 9),
            );
          return {
            plantCode,
            laborHours: round2(t.laborHours),
            laborDollars: round2(t.laborDollars),
            miles: round2(t.miles),
            mileageDollars: round2(t.mileageDollars),
            expenseDollars: round2(t.expenseDollars),
            orphanHours: round2(t.orphanHours),
            rateBreakdown,
            laborLines,
            total: round2(t.laborDollars + t.mileageDollars + t.expenseDollars),
          };
        })
        .sort((a, b) => a.plantCode.localeCompare(b.plantCode));
      const supplier = supplierByCode.get(supplierCode);
      return {
        supplierCode,
        supplierName: supplier?.Title || supplierCode,
        plants: plantsOut,
        supplierTotal: round2(
          plantsOut.reduce((acc, p) => acc + p.total, 0),
        ),
      };
    })
    .sort((a, b) => a.supplierName.localeCompare(b.supplierName));

  const grandTotal = round2(
    suppliersOut.reduce((acc, s) => acc + s.supplierTotal, 0),
  );
  // Grand-total hours across every supplier/plant in the period.
  // Includes priced labor hours AND unpriced "orphan" hours (someone
  // still worked them — they just need a rate on file before they can
  // be billed). Mileage and expenses aren't "hours" and stay out.
  const grandTotalHours = round2(
    suppliersOut.reduce(
      (acc, s) =>
        acc +
        s.plants.reduce(
          (pacc, p) => pacc + p.laborHours + p.orphanHours,
          0,
        ),
      0,
    ),
  );
  // Grand totals for the two non-labor billing categories. Pulled
  // out of the same per-plant aggregates so the headline tiles match
  // the per-supplier table totals exactly. Miles tracked alongside
  // the mileage dollars so the tile can show both the dollar value
  // and the underlying miles.
  // Grand-total COMMISSION hours — same rows as grandTotalHours, but OT
  // (Overage) hours count at time-and-a-half (Standard / Other stay 1:1).
  // Used for commission calculations where overtime earns 1.5×. Orphan
  // (unpriced) hours are counted 1:1 since their coverage type isn't
  // split out. (per Jillian, Sep 2026.)
  const grandTotalCommissionHours = round2(
    suppliersOut.reduce(
      (acc, s) =>
        acc +
        s.plants.reduce((pacc, p) => {
          const commissionLabor = (p.laborLines || []).reduce(
            (h, l) =>
              h +
              (l.coverageType === 'Overage'
                ? l.hours * OT_RATE_MULTIPLIER
                : l.hours),
            0,
          );
          return pacc + commissionLabor + p.orphanHours;
        }, 0),
      0,
    ),
  );
  const grandTotalExpenseDollars = round2(
    suppliersOut.reduce(
      (acc, s) =>
        acc + s.plants.reduce((pacc, p) => pacc + p.expenseDollars, 0),
      0,
    ),
  );
  const grandTotalMileageDollars = round2(
    suppliersOut.reduce(
      (acc, s) =>
        acc + s.plants.reduce((pacc, p) => pacc + p.mileageDollars, 0),
      0,
    ),
  );
  const grandTotalMiles = round2(
    suppliersOut.reduce(
      (acc, s) => acc + s.plants.reduce((pacc, p) => pacc + p.miles, 0),
      0,
    ),
  );

  const missingRatePairs = [...missingRateKeys].map((k) => {
    const [supplierCode, plantCode] = k.split('|');
    return { supplierCode, plantCode };
  });

  // Active-but-not-billed: every (supplier, plant) pair where the
  // supplier record marks coverageStatus = 'Active' should produce
  // billable activity in the period. If it doesn't, surface it so
  // the Director can chase down a missing timesheet, a delayed
  // approval, or an Active flag that should have been moved to
  // Paused/Closed. Built by walking the supplier list and removing
  // any pair that produced a bucket in suppliersOut.
  const billedPairKeys = new Set();
  for (const s of suppliersOut) {
    for (const p of s.plants) {
      billedPairKeys.add(
        `${String(s.supplierCode || '').toUpperCase()}|${String(p.plantCode || '').toUpperCase()}`,
      );
    }
  }
  const unbilledActivePairs = [];
  for (const supplier of suppliers || []) {
    const supplierCode = String(supplier.SupplierCode || '').trim();
    if (!supplierCode) continue;
    const supplierName = supplier.Title || supplierCode;
    const assignments = Array.isArray(supplier.plantAssignments)
      ? supplier.plantAssignments
      : [];
    for (const pa of assignments) {
      if ((pa.coverageStatus || '') !== 'Active') continue;
      const plantCode = String(pa.plantCode || '').trim();
      if (!plantCode) continue;
      const key = `${supplierCode.toUpperCase()}|${plantCode.toUpperCase()}`;
      if (billedPairKeys.has(key)) continue;
      unbilledActivePairs.push({
        supplierCode,
        supplierName,
        plantCode,
      });
    }
  }
  unbilledActivePairs.sort((a, b) => {
    const byName = a.supplierName.localeCompare(b.supplierName);
    if (byName !== 0) return byName;
    return a.plantCode.localeCompare(b.plantCode);
  });

  // Flatten the team-member map. Each entry resolves its supplier
  // display name off the already-built supplierByCode map so the UI
  // doesn't have to do a second lookup. Sorted by submitter name →
  // supplier name → plant code so the table scans cleanly.
  const teamOut = [...teamAgg.values()]
    .map((t) => {
      const total = round2(t.laborDollars + t.mileageDollars + t.expenseDollars);
      const supplier = supplierByCode.get(t.supplierCode);
      // Sort the collected submission timestamps so the UI can show
      // "earliest" and "latest" cleanly. Storing both lets the page
      // render a single date when they match, or a range otherwise.
      const submittedSorted = [...t.submittedAts].sort();
      // Primary timesheet id for the date-cell deep-link — the row
      // with the earliest SubmittedAt. When multiple timesheets feed
      // a single team row (e.g. two weeks of work for the same
      // supplier-plant pair), a single link target is the simplest
      // affordance; the user lands on the earliest timesheet and
      // can navigate from there.
      const idEntries = [...t.timesheetIds.entries()].sort(
        (a, b) => String(a[1]).localeCompare(String(b[1])),
      );
      const primaryTimesheetId = idEntries[0]?.[0] || '';
      const allTimesheetIds = idEntries.map(([id]) => id);
      return {
        submitterKey: t.submitterKey,
        submitterName: t.submitterName,
        submitterEmail: t.submitterEmail,
        supplierCode: t.supplierCode,
        supplierName: supplier?.Title || t.supplierCode,
        plantCode: t.plantCode,
        laborHours: round2(t.laborHours),
        laborDollars: round2(t.laborDollars),
        miles: round2(t.miles),
        mileageDollars: round2(t.mileageDollars),
        expenseDollars: round2(t.expenseDollars),
        orphanHours: round2(t.orphanHours),
        submittedAtEarliest: submittedSorted[0] || '',
        submittedAtLatest: submittedSorted[submittedSorted.length - 1] || '',
        submittedCount: submittedSorted.length,
        primaryTimesheetId,
        allTimesheetIds,
        total,
      };
    })
    .sort((a, b) => {
      const byName = a.submitterName.localeCompare(b.submitterName);
      if (byName !== 0) return byName;
      const bySupplier = a.supplierName.localeCompare(b.supplierName);
      if (bySupplier !== 0) return bySupplier;
      return a.plantCode.localeCompare(b.plantCode);
    });

  // Flatten the RM map into a display-friendly array. Each bucket
  // carries a Set of submitters and plant codes so the table can show
  // counts or the first few names without a second pass.
  const rmOut = [...rmAgg.values()]
    .map((r) => ({
      regionalManager: r.regionalManager,
      submitters: [...r.submitters].sort((a, b) => a.localeCompare(b)),
      plantCodes: [...r.plantCodes].sort(),
      laborHours: round2(r.laborHours),
      laborDollars: round2(r.laborDollars),
      miles: round2(r.miles),
      mileageDollars: round2(r.mileageDollars),
      expenseDollars: round2(r.expenseDollars),
      orphanHours: round2(r.orphanHours),
      total: round2(r.laborDollars + r.mileageDollars + r.expenseDollars),
    }))
    .sort((a, b) => a.regionalManager.localeCompare(b.regionalManager));

  // Flatten the LM map. Same shape as the RM rollup — the UI can
  // reuse most of the table markup. "Unassigned" always sorts last so
  // named LMs scan first.
  const lmOut = [...lmAgg.values()]
    .map((l) => ({
      liaisonManager: l.liaisonManager,
      submitters: [...l.submitters].sort((a, b) => a.localeCompare(b)),
      plantCodes: [...l.plantCodes].sort(),
      laborHours: round2(l.laborHours),
      laborDollars: round2(l.laborDollars),
      miles: round2(l.miles),
      mileageDollars: round2(l.mileageDollars),
      expenseDollars: round2(l.expenseDollars),
      orphanHours: round2(l.orphanHours),
      total: round2(l.laborDollars + l.mileageDollars + l.expenseDollars),
    }))
    .sort((a, b) => {
      if (a.liaisonManager === 'Unassigned') return 1;
      if (b.liaisonManager === 'Unassigned') return -1;
      return a.liaisonManager.localeCompare(b.liaisonManager);
    });

  return {
    suppliers: suppliersOut,
    team: teamOut,
    regionalManagers: rmOut,
    liaisonManagers: lmOut,
    grandTotal,
    grandTotalHours,
    grandTotalCommissionHours,
    grandTotalExpenseDollars,
    grandTotalMileageDollars,
    grandTotalMiles,
    range: { startDate: startIso, endDate: endIso },
    missingRatePairs,
    unbilledActivePairs,
    mileageRateUsed: MILEAGE_RATE_USD_PER_MILE,
  };
}

/**
 * Quick date-range presets for the Billing dashboard. Returns
 * { startDate, endDate } as YYYY-MM-DD strings.
 */
export function presetRange(preset) {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  if (preset === 'this-month') {
    return {
      startDate: toIso(new Date(y, m, 1)),
      endDate: toIso(new Date(y, m + 1, 0)),
    };
  }
  if (preset === 'last-month') {
    return {
      startDate: toIso(new Date(y, m - 1, 1)),
      endDate: toIso(new Date(y, m, 0)),
    };
  }
  if (preset === 'this-week') {
    const monday = mondayOfWeek(today);
    return {
      startDate: toIso(monday),
      endDate: toIso(
        new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6),
      ),
    };
  }
  if (preset === 'last-week') {
    const monday = mondayOfWeek(today);
    const prev = new Date(
      monday.getFullYear(),
      monday.getMonth(),
      monday.getDate() - 7,
    );
    return {
      startDate: toIso(prev),
      endDate: toIso(
        new Date(prev.getFullYear(), prev.getMonth(), prev.getDate() + 6),
      ),
    };
  }
  // "Past N weeks" — N complete Mon-Sun weeks ending with the
  // current week (so this week + the previous N-1 weeks are
  // included). Aligns with the existing week-start convention so the
  // start lands on a Monday and the end on the following Sunday.
  if (preset === 'past-4-weeks' || preset === 'past-5-weeks') {
    const weeks = preset === 'past-4-weeks' ? 4 : 5;
    const monday = mondayOfWeek(today);
    const start = new Date(
      monday.getFullYear(),
      monday.getMonth(),
      monday.getDate() - 7 * (weeks - 1),
    );
    const end = new Date(
      monday.getFullYear(),
      monday.getMonth(),
      monday.getDate() + 6,
    );
    return { startDate: toIso(start), endDate: toIso(end) };
  }
  if (preset === 'ytd') {
    return {
      startDate: toIso(new Date(y, 0, 1)),
      endDate: toIso(today),
    };
  }
  // Last Year — Jan 1 → Dec 31 of the previous calendar year.
  // Picked over a rolling 365-day window so the range always lines up
  // cleanly with annual reports / 1099 cycles and reads consistently
  // ("2025" not "May 14 2025 — May 13 2026") in the range label.
  if (preset === 'last-year') {
    return {
      startDate: toIso(new Date(y - 1, 0, 1)),
      endDate: toIso(new Date(y - 1, 11, 31)),
    };
  }
  return { startDate: '', endDate: '' };
}

// ---- Date helpers -------------------------------------------------

function toIso(value) {
  if (!value) return '';
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
}

function inRange(iso, startIso, endIso) {
  if (!iso) return false;
  if (startIso && iso < startIso) return false;
  if (endIso && iso > endIso) return false;
  return true;
}

function addDays(iso, days) {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + days);
  const y = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function mondayOfWeek(d) {
  // Kept the name for its callers; now returns the most-recent SUNDAY
  // (start of the Sun→Sat billing week, Aug 2026). In sync with
  // weekStartFromDate in timesheets.js.
  const day = d.getDay(); // 0=Sun, 1=Mon, ...
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
}

// ---- Legacy Billing-list CRUD (pre-timesheets) -------------------
// Left in place while the rewritten Billing page rolls out; can be
// deleted once Invoicing is live and no caller references them.

const LIST = 'billing';

export const BILLING_STATUSES = ['Submitted', 'Approved', 'Rejected', 'Invoiced'];

export async function listBilling(opts) {
  return listItems(LIST, { orderby: 'fields/WorkDate desc', ...opts });
}

export const getBillingEntry = (id) => getItem(LIST, id);
export const createBillingEntry = (model) => createItem(LIST, model);
export const updateBillingEntry = (id, model) => updateItem(LIST, id, model);
export const deleteBillingEntry = (id) => deleteItem(LIST, id);

export const approveBilling = (id, approverEmail) =>
  updateItem(LIST, id, {
    Status: 'Approved',
    ApprovedBy: approverEmail,
    ApprovedAt: new Date().toISOString(),
  });

export const rejectBilling = (id, approverEmail, reason) =>
  updateItem(LIST, id, {
    Status: 'Rejected',
    ApprovedBy: approverEmail,
    ApprovedAt: new Date().toISOString(),
    RejectionReason: reason || '',
  });
